import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Phone, Mail, Clock, Image, AlertCircle, CheckCircle, Trash2, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_business-match-hub-1/artifacts/ly54u1mk_7A4F7B0F-B2FC-4B29-8962-F57D33FF73A6.png";

// Simple password protection
const ADMIN_PASSWORD = "Black0ne2!";

export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const [activeTab, setActiveTab] = useState('estimates');
  const [estimates, setEstimates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [demos, setDemos] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [estimatesRes, contactsRes, demosRes, newslettersRes] = await Promise.all([
        axios.get(`${API}/estimate`).catch(() => ({ data: [] })),
        axios.get(`${API}/contact`).catch(() => ({ data: [] })),
        axios.get(`${API}/demo-request`).catch(() => ({ data: [] })),
        axios.get(`${API}/newsletter`).catch(() => ({ data: [] })),
      ]);
      
      setEstimates(estimatesRes.data || []);
      setContacts(contactsRes.data || []);
      setDemos(demosRes.data || []);
      setNewsletters(newslettersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent': return 'bg-red-500/20 text-red-500 border-red-500/50';
      case 'standard': return 'bg-orange-500/20 text-orange-500 border-orange-500/50';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50';
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-zinc-950 border border-zinc-800 p-8 max-w-sm w-full">
          <img src={LOGO_URL} alt="23rd Industries" className="h-12 mx-auto mb-6" />
          <h1 className="text-2xl uppercase tracking-wide font-bold text-white text-center mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Admin Access
          </h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full bg-black border border-zinc-800 text-white p-4 mb-4 focus:border-orange-500 focus:outline-none"
              data-testid="admin-password-input"
            />
            {passwordError && (
              <p className="text-red-500 text-sm mb-4">{passwordError}</p>
            )}
            <button
              type="submit"
              className="w-full bg-orange-600 text-black py-3 uppercase tracking-widest font-bold hover:bg-orange-500 transition-colors"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="admin-login-btn"
            >
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'estimates', label: 'Estimates', count: estimates.length },
    { id: 'contacts', label: 'Contact Forms', count: contacts.length },
    { id: 'demos', label: 'Demo Requests', count: demos.length },
    { id: 'newsletters', label: 'Newsletter', count: newsletters.length },
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="admin-page">
      {/* Header */}
      <header className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="23rd Industries" className="h-8" />
            <span className="text-white font-bold uppercase tracking-wide" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Admin Dashboard
            </span>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 text-zinc-400 hover:text-orange-500 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto flex overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 text-sm uppercase tracking-widest font-mono whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-orange-500 text-orange-500'
                  : 'border-transparent text-zinc-500 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                activeTab === tab.id ? 'bg-orange-500 text-black' : 'bg-zinc-800 text-zinc-400'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Loading submissions...</p>
          </div>
        ) : (
          <>
            {/* Estimates Tab */}
            {activeTab === 'estimates' && (
              <div className="space-y-4">
                {estimates.length === 0 ? (
                  <div className="text-center py-12 bg-zinc-950 border border-zinc-800">
                    <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">No estimate requests yet</p>
                  </div>
                ) : (
                  estimates.slice().reverse().map((est) => (
                    <motion.div
                      key={est.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-zinc-950 border border-zinc-800 p-4 md:p-6 hover:border-zinc-700 transition-colors"
                      data-testid={`estimate-${est.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                              {est.name}
                            </h3>
                            <span className={`text-xs px-2 py-1 border uppercase tracking-wider ${getUrgencyColor(est.urgency)}`}>
                              {est.urgency}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-3">
                            <a href={`tel:${est.phone}`} className="flex items-center gap-1 hover:text-orange-500">
                              <Phone className="w-4 h-4" /> {est.phone}
                            </a>
                            <a href={`mailto:${est.email}`} className="flex items-center gap-1 hover:text-orange-500">
                              <Mail className="w-4 h-4" /> {est.email}
                            </a>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" /> {formatDate(est.created_at)}
                            </span>
                          </div>
                          
                          {est.company && (
                            <p className="text-zinc-500 text-sm mb-2">Company: {est.company}</p>
                          )}
                          
                          <div className="flex items-center gap-2 mb-3">
                            <span className="bg-orange-500/20 text-orange-500 text-xs px-3 py-1 uppercase tracking-wider">
                              {est.service_type}
                            </span>
                            <span className="text-zinc-600 text-xs">
                              Prefers: {est.preferred_contact}
                            </span>
                          </div>
                          
                          <p className="text-zinc-300 bg-black p-3 border border-zinc-800">
                            {est.description}
                          </p>
                          
                          {/* Images */}
                          {est.image_urls && est.image_urls.length > 0 && (
                            <div className="mt-4">
                              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                <Image className="w-3 h-3" /> {est.image_urls.length} Photo(s) Attached
                              </p>
                              <div className="flex gap-2 flex-wrap">
                                {est.image_urls.map((url, idx) => (
                                  <a
                                    key={idx}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-20 h-20 bg-zinc-900 border border-zinc-800 hover:border-orange-500 transition-colors overflow-hidden"
                                  >
                                    <img src={url} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="space-y-4">
                {contacts.length === 0 ? (
                  <div className="text-center py-12 bg-zinc-950 border border-zinc-800">
                    <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">No contact form submissions yet</p>
                  </div>
                ) : (
                  contacts.slice().reverse().map((contact) => (
                    <motion.div
                      key={contact.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-zinc-950 border border-zinc-800 p-4 md:p-6"
                      data-testid={`contact-${contact.id}`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                          {contact.name}
                        </h3>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-3">
                        {contact.phone && (
                          <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-orange-500">
                            <Phone className="w-4 h-4" /> {contact.phone}
                          </a>
                        )}
                        <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-orange-500">
                          <Mail className="w-4 h-4" /> {contact.email}
                        </a>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" /> {formatDate(contact.created_at)}
                        </span>
                      </div>
                      
                      {contact.company && (
                        <p className="text-zinc-500 text-sm mb-2">Company: {contact.company}</p>
                      )}
                      
                      {contact.service_interest && (
                        <span className="bg-orange-500/20 text-orange-500 text-xs px-3 py-1 uppercase tracking-wider inline-block mb-3">
                          {contact.service_interest}
                        </span>
                      )}
                      
                      <p className="text-zinc-300 bg-black p-3 border border-zinc-800">
                        {contact.message}
                      </p>
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* Demos Tab */}
            {activeTab === 'demos' && (
              <div className="space-y-4">
                {demos.length === 0 ? (
                  <div className="text-center py-12 bg-zinc-950 border border-zinc-800">
                    <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">No demo requests yet</p>
                  </div>
                ) : (
                  demos.slice().reverse().map((demo) => (
                    <motion.div
                      key={demo.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-zinc-950 border border-zinc-800 p-4 md:p-6"
                      data-testid={`demo-${demo.id}`}
                    >
                      <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                        {demo.name}
                      </h3>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-3">
                        <a href={`tel:${demo.phone}`} className="flex items-center gap-1 hover:text-orange-500">
                          <Phone className="w-4 h-4" /> {demo.phone}
                        </a>
                        <a href={`mailto:${demo.email}`} className="flex items-center gap-1 hover:text-orange-500">
                          <Mail className="w-4 h-4" /> {demo.email}
                        </a>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" /> {formatDate(demo.created_at)}
                        </span>
                      </div>
                      
                      <p className="text-zinc-500 text-sm">Company: {demo.company}</p>
                      {demo.fleet_size && <p className="text-zinc-500 text-sm">Fleet Size: {demo.fleet_size}</p>}
                      {demo.preferred_date && <p className="text-zinc-500 text-sm">Preferred Date: {demo.preferred_date}</p>}
                      {demo.notes && (
                        <p className="text-zinc-300 bg-black p-3 border border-zinc-800 mt-3">
                          {demo.notes}
                        </p>
                      )}
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* Newsletter Tab */}
            {activeTab === 'newsletters' && (
              <div className="space-y-4">
                {newsletters.length === 0 ? (
                  <div className="text-center py-12 bg-zinc-950 border border-zinc-800">
                    <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">No newsletter subscribers yet</p>
                  </div>
                ) : (
                  <div className="bg-zinc-950 border border-zinc-800 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-black">
                        <tr>
                          <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-mono">Email</th>
                          <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-mono">Subscribed</th>
                          <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-mono">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newsletters.slice().reverse().map((sub) => (
                          <tr key={sub.id} className="border-t border-zinc-800">
                            <td className="p-4 text-white">{sub.email}</td>
                            <td className="p-4 text-zinc-400 text-sm">{formatDate(sub.subscribed_at)}</td>
                            <td className="p-4">
                              <span className={`text-xs px-2 py-1 ${sub.active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                                {sub.active ? 'Active' : 'Unsubscribed'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Stats Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-950 border-t border-zinc-800 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-sm">
          <div className="flex gap-6">
            <span className="text-zinc-500">
              Total Leads: <span className="text-orange-500 font-bold">{estimates.length + contacts.length + demos.length}</span>
            </span>
          </div>
          <button
            onClick={() => setIsAuthenticated(false)}
            className="text-zinc-500 hover:text-white transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};
